import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import MobileLayout from "../../components/MobileLayout";
import { useAuth } from "../../context/AuthContext";
import api from "../../utils/api";
import { Building, Copy, Check, AlertCircle } from "lucide-react";

const Deposits = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();

  const [plans, setPlans] = useState([]);
  const [bankDetails, setBankDetails] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(
    searchParams.get("planId") || ""
  );

  const [amount, setAmount] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [copied, setCopied] = useState("");
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [depositSuccess, setDepositSuccess] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, configRes] = await Promise.all([
        api.get("/plans"),
        api.get("/config/bank-details"),
      ]);
      console.log("Plans:", plansRes.data, "Bank Details:", configRes.data);
      setPlans(plansRes.data);
      setBankDetails(configRes.data);
    } catch (err) {
      console.error("Failed to fetch deposit data:", err);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopied(field);
    setTimeout(() => setCopied(""), 2000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await api.post("/deposits", {
        planId: selectedPlan || null,
        amount: parseFloat(amount),
        transactionId,
      });
      setDepositSuccess(true);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to submit deposit");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <MobileLayout showBottomNav={false}>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent" />
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout showBottomNav={false}>
      <div className="p-4 space-y-4 pb-8">

        {depositSuccess ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Check className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Deposit Submitted</h2>
            <p className="text-gray-600 mb-6">
              Your deposit request is under verification.
            </p>
            <button
              onClick={() => navigate("/home")}
              className="bg-primary text-white py-3 px-6 rounded-lg"
            >
              Go to Home
            </button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div>
              <button
                onClick={() => navigate(-1)}
                className="text-primary text-sm mb-2"
              >
                ← Back
              </button>
              <h1 className="text-2xl font-bold">Make Deposit</h1>
              <p className="text-sm text-gray-600">Manual bank transfer</p>
            </div>

            {/* OPTIONAL KYC INFO */}
            {user?.kycStatus !== "Approved" && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 rounded-lg p-4">
                <div className="flex gap-2">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                  <p className="text-xs text-gray-700">
                    KYC is optional. Completing KYC unlocks higher limits and faster approvals.
                  </p>
                </div>
              </div>
            )}

            {/* Bank Details */}
            <div className="bg-white rounded-xl p-4 border">
              <div className="flex items-center gap-2 mb-4">
                <Building className="w-5 h-5 text-primary" />
                <h3 className="font-semibold">Bank Transfer Details</h3>
              </div>

              {[
                ["Account Name", bankDetails?.bank_account_name, "name"],
                ["Account Number", bankDetails?.bank_account_number, "account"],
                ["IFSC Code", bankDetails?.bank_ifsc, "ifsc"],
                ["UPI ID", bankDetails?.upi_id, "upi"],
              ].map(([label, value, key]) => (
                <div key={key} className="bg-gray-50 rounded-lg p-3 mb-2">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-xs text-gray-600">{label}</p>
                      <p className="font-mono text-sm font-semibold">{value}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(value, key)}
                      className="p-2"
                    >
                      {copied === key ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-600" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Deposit Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="bg-white rounded-xl p-4 border">
                <h3 className="font-semibold mb-4">Deposit Details</h3>

                <div className="space-y-4">
                  <input
                    type="number"
                    placeholder="Amount"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    required
                    className="w-full p-3 border rounded-lg"
                  />

                  <input
                    type="text"
                    placeholder="Transaction ID / UTR"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    required
                    className="w-full p-3 border rounded-lg"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-primary text-white py-4 rounded-lg disabled:bg-gray-400"
              >
                {submitting ? "Submitting..." : "Submit Deposit Request"}
              </button>
            </form>
          </>
        )}
      </div>
    </MobileLayout>
  );
};

export default Deposits;
